// Base implementation for voice providers with common functionality

import {
	type ConnectionState,
	type IVoiceAgentProvider,
	type ProviderConfig,
	type TranscriptSegment,
	type VoiceProviderEvents,
	ProviderPhases,
} from '#app/utils/voice-providers/types.ts'

export abstract class BaseVoiceProvider implements IVoiceAgentProvider {
	protected connectionState: ConnectionState = {
		isConnected: false,
		isConnecting: false,
		isRecording: false,
		isMuted: false,
		conversationId: null,
		currentPhase: ProviderPhases.IDLE,
		progress: 0,
	}

	protected eventListeners: VoiceProviderEvents = {}
	protected transcriptSegments: TranscriptSegment[] = []
	protected fullTranscript = ''
	protected config: ProviderConfig = {}

	constructor(
		protected providerName: string,
		protected providerId: string,
	) {}

	// Abstract methods that must be implemented by providers
	abstract connect(config?: ProviderConfig): Promise<string>
	abstract disconnect(): Promise<void>
	abstract isSupported(): Promise<boolean>
	abstract mute(): void
	abstract unmute(): void

	// Provider identification
	getProviderName(): string {
		return this.providerName
	}

	getProviderId(): string {
		return this.providerId
	}

	// State queries
	getConnectionState(): ConnectionState {
		return { ...this.connectionState }
	}

	isConnected(): boolean {
		return this.connectionState.isConnected
	}

	isRecording(): boolean {
		return this.connectionState.isRecording
	}

	isMuted(): boolean {
		return this.connectionState.isMuted
	}

	// Audio controls
	toggleMute(): void {
		if (this.isMuted()) {
			this.unmute()
		} else {
			this.mute()
		}
	}

	// Event management
	setEventListeners(events: VoiceProviderEvents): void {
		this.eventListeners = { ...this.eventListeners, ...events }
	}

	removeEventListeners(): void {
		this.eventListeners = {}
	}

	// Transcript access
	getFullTranscript(): string {
		return this.fullTranscript
	}

	getTranscriptSegments(): TranscriptSegment[] {
		return [...this.transcriptSegments]
	}

	// Protected helper methods for derived classes
	protected updateConnectionState(updates: Partial<ConnectionState>): void {
		this.connectionState = { ...this.connectionState, ...updates }
		this.eventListeners.onStateChange?.(this.getConnectionState())
	}

	protected addTranscriptSegment(segment: TranscriptSegment): void {
		this.transcriptSegments.push(segment)

		// Update full transcript
		if (segment.speaker === 'agent') {
			this.fullTranscript += `\n\nAgent: ${segment.text}`
		} else {
			this.fullTranscript += `\n\nCaller: ${segment.text}`
		}

		// Notify listeners
		this.eventListeners.onTranscript?.({
			segments: this.getTranscriptSegments(),
			fullTranscript: this.fullTranscript,
			lastSegment: segment,
		})
	}

	protected handleError(error: Error): void {
		this.updateConnectionState({
			isConnected: false,
			isConnecting: false,
			isRecording: false,
			currentPhase: ProviderPhases.ERROR,
		})
		this.eventListeners.onError?.(error)
	}

	protected reset(): void {
		this.connectionState = {
			isConnected: false,
			isConnecting: false,
			isRecording: false,
			isMuted: false,
			conversationId: null,
			currentPhase: ProviderPhases.IDLE,
			progress: 0,
		}
		this.transcriptSegments = []
		this.fullTranscript = ''
	}

	// Cleanup
	destroy(): void {
		void this.disconnect()
		this.removeEventListeners()
		this.reset()
	}
}
